/**
 * # MenuListItem
 *
 * A component that represents a list item with hover and click states. It can display text, icons, and handle click events.
 *
 * ## Props
 *
 * - `as` (optional): The HTML element or React component to be rendered as the root element of the component. Default is `div`.
 * - `children` (optional): The content to be rendered inside the MenuListItem.
 * - `className` (optional): Additional CSS class names to be applied to the MenuListItem.
 * - `clickable` (optional): Determines if the MenuListItem is clickable. Default is `true`.
 * - `hoverable` (optional): Determines if the MenuListItem is hoverable. Default is `true`.
 * - `isCategoryItem` (optional): Determines if the MenuListItem is a category item. Default is `false`.
 * - `iconButtonProps` (optional): Additional props to be passed to the IconButton component used in the MenuListItem.
 * - `text` (optional): The text to be displayed in the MenuListItem.
 * - `icon` (optional): The icon to be displayed in the MenuListItem.
 * - `onMenuItemClick` (optional): The click event handler for the MenuListItem.
 * - `onIconButtonClick` (optional): The click event handler for the IconButton component used in the MenuListItem.
 * - `selected` (optional): Determines if the MenuListItem is selected. Default is `false`.
 * - `disableWhenSelected` (optional): Determines if the MenuListItem should be disabled when selected. Default is `true`.
 * - `indentLevel` (optional): The indent level of the MenuListItem. Can be '0', '1', or '2'.
 *
 * ## Usage
 *
 * ```tsx
 * import { MenuListItem } from './MenuListItem';
 *
 * const MyComponent = () => {
 *   return (
 *     <MenuListItem
 *       text="Menu Item"
 *       icon="icon-name"
 *       onMenuItemClick={() => {
 *         console.log('Menu item clicked');
 *       }}
 *     />
 *   );
 * };
 * ```
 */

import classnames from 'classnames';

import type {ElementType, KeyboardEvent, MouseEvent} from 'react';
import React, {useImperativeHandle, useRef} from 'react';
import {polymorphicForwardRef} from '../../utils/polymorphicForwardRef';

import type {IconButtonProps} from '@albert-io/atomic/molecules/IconButton/IconButton.react';
import IconButton from '@albert-io/atomic/molecules/IconButton/IconButton.react';

import type {IconProp} from '../Icon/Icon.react';
import Icon from '../Icon/Icon.react';

import type {Props as MenuListItemCategoryTextProps} from './MenuListItemCategoryText';
import MenuListItemCategoryText from './MenuListItemCategoryText';
import type {Props as MenuListItemRegularTextProps} from './MenuListItemRegularText';
import MenuListItemRegularText from './MenuListItemRegularText';

import './menu-list-item.scss';

type HandleButtonClickType = () => void;

// requires button label along with icon button click handler for aria support
export type ConditionalButtonClickProps =
  | {
      iconButtonLabel: string;
      iconButtonProps?: Partial<IconButtonProps<'button'>>;
      onIconButtonClick?: HandleButtonClickType;
    }
  | {
      iconButtonLabel?: never;
      iconButtonProps?: never;
      onIconButtonClick?: never;
    };

interface RegularTextProps {
  categoryTextProps?: never;
  isCategoryItem: false;
  regularTextProps?: Partial<MenuListItemRegularTextProps>;
  text?: string;
}

interface CategoryTextProps {
  categoryTextProps?: Partial<MenuListItemCategoryTextProps>;
  isCategoryItem: true;
  regularTextProps?: never;
  text?: string;
}

export type ConditionalTextProps = RegularTextProps | CategoryTextProps;

type ConditionalProps =
  | ({
      iconButtonLabel: never;
      iconButtonProps?: never;
      onIconButtonClick?: never;
      text?: never;
      icon?: never;
    } & PropsWithChildrenOptional)
  | ({
      children?: never;
      icon?: IconProp;
    } & ConditionalButtonClickProps &
      ConditionalTextProps);

export interface CommonProps<C extends ElementType = 'div'> extends PropsWithClassNameOptional {
  as?: C;
  clickable?: boolean;
  disabled?: boolean;
  disableWhenSelected?: boolean;
  hoverable?: boolean;
  indentLevel?: '0' | '1' | '2';
  isCategoryItem?: boolean;
  // onClick applied to entire item
  onMenuItemClick?: () => void; // TODO: determine if this needs to pass an event
  selected?: boolean;
  textClassNames?: string;
}

const indentMap = {
  '0': 'pl-x2',
  '1': 'pl-x4',
  '2': 'pl-x6'
};

export type Props<C extends ElementType> = PolymorphicComponentProps<
  C,
  CommonProps<C> & ConditionalProps
>;

function MenuListItem<C extends ElementType = 'div'>(
  {
    as,
    categoryTextProps,
    children,
    className,
    clickable = true,
    disabled = false,
    disableWhenSelected = false,
    hoverable = true,
    icon,
    iconButtonLabel,
    iconButtonProps,
    indentLevel = '0',
    isCategoryItem,
    onIconButtonClick,
    onMenuItemClick,
    regularTextProps,
    selected = false,
    text = '',
    textClassNames,
    ...rest
  }: Props<C>,
  ref
) {
  const handleIconButtonClick = (e: MouseEvent) => {
    if (onIconButtonClick !== undefined) {
      onIconButtonClick();
    }

    e.stopPropagation();
  };

  const WrapperElement = as || 'div';
  const internalRef = useRef(null);

  // Forward the internal ref to the parent
  useImperativeHandle(ref, () => internalRef.current);

  function getChildren() {
    const textClasses = classnames(
      'py-x1-quarter',
      'items-center flex pr-x2 w-full',
      indentMap[indentLevel],
      textClassNames
    );

    return (
      <>
        {text !== '' && (
          <div
            className={classnames('flex grow', {
              'a-menu-list-item--hoverable': hoverable
            })}
          >
            {isCategoryItem ? (
              <MenuListItemCategoryText className={textClasses} {...categoryTextProps}>
                {text}
              </MenuListItemCategoryText>
            ) : (
              <MenuListItemRegularText
                className={textClasses}
                selected={selected}
                {...regularTextProps}
              >
                {text}
              </MenuListItemRegularText>
            )}
          </div>
        )}

        {icon !== undefined && (
          <div
            className={classnames('flex items-center justify-center', {
              'a-menu-list-item__decorative-icon': onIconButtonClick === undefined,
              'a-menu-list-item--hoverable': hoverable,
              'w-full': text === ''
            })}
          >
            {onIconButtonClick === undefined ? (
              <Icon icon={icon} />
            ) : (
              <IconButton
                label={iconButtonLabel}
                onClick={handleIconButtonClick}
                icon={icon}
                {...iconButtonProps}
              />
            )}
          </div>
        )}
      </>
    );
  }

  // Since we do have support for an iconButton to be a child we need this to make sure that keypresses on the iconButtons don't activate the parent
  const handleKeyPress = (e: KeyboardEvent) => {
    const keys = [' ', 'Enter'];
    if (global.document.activeElement === internalRef.current && keys.includes(e.key)) {
      e.preventDefault();
      onMenuItemClick?.();
    }
  };

  const shouldDisable = (disableWhenSelected && selected) || disabled;

  return (
    <WrapperElement
      className={classnames(
        className,
        'a-menu-list-item',
        'rounded-x1',
        'flex',
        'overflow-hidden',
        'relative',
        'no-underline',
        'w-full',
        {
          'a-menu-list-item--clickable': clickable,
          'a-menu-list-item--disabled': (disableWhenSelected && selected) || disabled,
          'a-menu-list-item--hoverable': hoverable && children !== undefined,
          'a-menu-list-item--segmented': text !== '' && icon !== undefined,
          'a-menu-list-item--selected': selected,
          'background-brand-subtle': selected,
          'content-brand': selected,
          'cursor-pointer': clickable,
          'justify-center': !text,
          'justify-between': text,
          'px-x2 py-x1': children !== undefined
        }
      )}
      ref={internalRef}
      data-name='menu-list-item'
      // @ts-expect-error -- `disabled` is only forwarded to wrappers that support it; plain divs get undefined
      disabled={WrapperElement === 'div' ? undefined : shouldDisable}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={shouldDisable ? () => {} : onMenuItemClick}
      onKeyDown={shouldDisable ? () => {} : handleKeyPress}
      {...rest}
    >
      {children ?? getChildren()}
    </WrapperElement>
  );
}

export default polymorphicForwardRef(MenuListItem, 'MenuListItem');
